import Environment from '@omni/kit/Environment';
import { AuthProviderList } from '@omni/kit/auth';
import KitIcon from '@omni/kit/components/KitIcon';
import { KitTab, KitTabGroup } from '@omni/kit/components/KitTabGroup';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import { useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import Colors from '@omni/kit/theming/Colors';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { RouteProp } from '@react-navigation/core/lib/typescript/src/types';
import { useIsFocused } from '@react-navigation/native';
import { ParamListBase } from '@react-navigation/routers';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useChatAuth } from '../../context/ChatAuthContext';
import { CHAT_TAB, PRAYER_TAB, THREADS_TAB } from '../../shared/Constants';
import {
  createChatHandler,
  setChannel,
} from '../../shared/redux/actions/ChatActions';
import {
  appKeySelector,
  channelListSelector,
  channelSelector,
  orgKeySelector,
  viewIsReadySelector,
} from '../../shared/redux/selectors';
import { getFullChannelUrl } from '../../utilities/chatUtilities';
import { sbGetGroupChannel } from '../../utilities/sendbird/channelFunctions';
import { sbHasUser } from '../../utilities/sendbird/userFunctions';
import AppLoadingView from '../components/AppLoadingView';
import ChatScreen from '../scenes/channel/ChatScreen';
import PrayerListScreen from '../scenes/channel/PrayerListScreen';
import ThreadListScreen from '../scenes/channel/ThreadListScreen';

const debug = require('debug')('omni:chat:navigation:ChatTabNavigator');

export interface RouteProps extends RouteProp<ParamListBase, string> {
  params: {
    channelId?: string;
  };
}

export default function ChatTabs({
  route,
}: {
  route: RouteProps;
}): JSX.Element | null {
  const { channelId } = route.params;
  const channel = useSelector(channelSelector);
  const channelList = useSelector(channelListSelector);
  const appKey = useSelector(appKeySelector);
  const orgKey = useSelector(orgKeySelector);

  const { isAuthenticated, isReady } = useChatAuth();

  const viewIsReady = useSelector(viewIsReadySelector);

  const isAuthenticatedWithSendbird =
    isAuthenticated && sbHasUser() && viewIsReady;

  const isFocused = useIsFocused();

  const dispatch = useDispatch();

  useEffect(() => {
    if (isFocused) {
      if (channel) {
        dispatch(createChatHandler(channel.url));
      }
    }
  }, [channel, dispatch, isFocused]);

  useEffect(() => {
    if (!channel && channelList && channelList.length > 0) {
      debug('No channel selected, so set the first one in the list');
      dispatch(setChannel(channelList[0]));
    } else if (channelId && isAuthenticatedWithSendbird) {
      const fetchChannel = async () => {
        // Load channel using ID
        try {
          const groupChannel = await sbGetGroupChannel(
            getFullChannelUrl(channelId)
          );
          dispatch(setChannel(groupChannel));
        } catch {}
      };
      fetchChannel();
    }
  }, [channel, channelId, channelList, dispatch, isAuthenticatedWithSendbird]);

  if (!isReady) {
    return null;
  }

  // Prompt user to login if the tokens have been cleared when a user
  // logged into another app in Web Messaging
  if (!isAuthenticated) {
    return (
      <AuthProviderList
        appKey={appKey}
        orgKey={orgKey}
        customReturnUrl={`${Environment.host}/${appKey}/auth`}
        targetAuthProviderId={SUBSPLASH_AUTH_PROVIDER_ID}
        openInNewTab
      />
    );
  }

  if (channel) {
    return (
      <KitTabGroup
        tabBarOptions={{
          showIcon: true,
          showLabel: false,
        }}
      >
        <KitTab
          name={CHAT_TAB}
          component={ChatScreen}
          options={{
            title: 'Chat',
            tabBarIcon: ({ color }) => (
              <KitIcon
                name='messaging'
                color={color ? color : colorForScheme({ default: Colors.N900 })}
                size={22}
              />
            ),
          }}
        />
        <KitTab
          name={PRAYER_TAB}
          component={PrayerListScreen}
          options={{
            title: 'Prayer',
            tabBarIcon: ({ color }) => (
              <KitIcon
                name='prayer'
                color={color ? color : colorForScheme({ default: Colors.N900 })}
                size={20}
              />
            ),
          }}
        />
        <KitTab
          name={THREADS_TAB}
          component={ThreadListScreen}
          options={{
            title: 'Threads',
            tabBarIcon: ({ color }) => (
              <KitIcon
                name='thread'
                color={color ? color : colorForScheme({ default: Colors.N900 })}
                size={22}
              />
            ),
          }}
        />
      </KitTabGroup>
    );
  }

  return <AppLoadingView screenProps={{ appKey }} />;
}
